import React, { useState, useEffect } from 'react'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useTranslation } from 'react-i18next'
import useAuth from 'routes/useAuth'
import {
    customDateFormat,
    customTimeFromDateTimeFormat,
    customTimeFormat,
    utcDateTimeToBahrainTime,
} from 'utils/common'
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { EllipsisVerticalIcon } from '@heroicons/react/16/solid'
import { EyeIcon, ArrowLeftIcon } from '@heroicons/react/20/solid'
import { RoutesOperationsIcon } from 'components/icons'
import { GET_CAPTAIN_ROUTE_BOAT_HISTORY_LIST } from 'graphql/operations/queries'
import loadingImage from '../../assets/images/boat-loading.gif'
import { useLazyQuery } from '@apollo/client'

import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function PassengerRecord() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { sessionData, isNSAUser } = useAuth()
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [historyList, setHistoryList] = useState(null)
    const [historyCount, setHistoryCount] = useState(0)
    const [activityDate, setActivityDate] = useState(
        localStorage.getItem('selectedPassengerRecordDate') || moment().format('YYYY-MM-DD')
    )
    const [tripType, setTripType] = useState('INL') // Default to International

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(historyCount / rowsPerPage)

    // Get Boat List
    const [getAssignmentHistoryList, { loading: historyListLoading, error: historyListError }] = useLazyQuery(
        GET_CAPTAIN_ROUTE_BOAT_HISTORY_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getCaptainRouteBoatHistory.success && data.getCaptainRouteBoatHistory.code === '200') {
                    const all = data.getCaptainRouteBoatHistory.history || []
                    // Filter by selected trip type
                    const filtered = tripType === 'ALL' 
                        ? all 
                        : all.filter(h => h?.route?.master_route?.route_type?.code === tripType)
                    setHistoryList(filtered)
                    setHistoryCount(filtered.length)
                }
            },
        }
    )

    // Search boats Boat List
    const searchAssignmentHistory = () => {
        getAssignmentHistoryList({
            variables: {
                captainId: null,
                routeId: null,
                boatId: null,
                historyId: null,
                activityDate: activityDate,
            },
        })
    }

    useEffect(() => {
        searchAssignmentHistory()
    }, [page, rowsPerPage, activityDate, tripType])

    // Check if user can see trip type filter (non-NSA users)
    const canSeeTripTypeFilter = sessionData?.['cognito:groups'] && !isNSAUser()

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
                <div></div>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap mb-4">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                    <RoutesOperationsIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                </div>
                                <div>
                                    <span className="ms-3">{t('Trip Manifest')}</span>
                                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                        {t(
                                            'View passenger records and trip details for scheduled boat routes'
                                        )}
                                    </p>
                                </div>
                            </h1>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            <div className="mb-6 mt-6">
                <div className="flex items-center gap-4 flex-wrap">
                    {/* Trip Type Filter - Only for non-NSA users */}
                    {canSeeTripTypeFilter && (
                        <div className="flex items-center gap-2">
                            <label
                                htmlFor="tripType"
                                className="block text-sm font-medium text-gray-700 whitespace-nowrap"
                            >
                                {t('Trip Type')}
                            </label>
                            <select
                                id="tripType"
                                value={tripType}
                                onChange={(e) => {
                                    setTripType(e.target.value)
                                    setPage(0)
                                }}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                            >
                                <option value="INL">{t('International')}</option>
                                <option value="DMC">{t('Domestic')}</option>
                                <option value="ALL">{t('All')}</option>
                            </select>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label
                            htmlFor="activityDate"
                            className="block text-sm font-medium text-gray-700 whitespace-nowrap"
                        >
                            {t('Select Date')}
                        </label>
                        <Flatpickr
                            value={activityDate}
                            placeholder="dd/mm/yyyy"
                            onChange={date => {
                                // Use local time to avoid UTC shifting to previous day
                                const selectedDate = moment(date[0]).format('YYYY-MM-DD')
                                setActivityDate(selectedDate)
                                setPage(0)
                                localStorage.setItem('selectedPassengerRecordDate', selectedDate)
                            }}
                            options={{
                                altInput: true,
                                altFormat: 'Y/m/d',
                                // Use ISO-like with dashes for internal value to match API expected 'YYYY-MM-DD'
                                dateFormat: 'Y-m-d',
                                // Provide min/max as strings to avoid timezone edge cases
                                maxDate: moment().add(30, 'days').format('YYYY-MM-DD'),
                                enableTime: false,
                                allowInput: false,
                                disableMobile: true,
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
            </div>

            {historyListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {historyListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch records!')}</div>
                </div>
            )}
            {!historyListLoading && !historyListError && historyList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!historyListLoading && !historyListError && historyList?.length > 0 && (
                <>
                    <div className="table-custom flex justify-center items-center bg-white p-0">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Date')}</TableHeader>
                                    <TableHeader>{t('Route')}</TableHeader>
                                    <TableHeader>{t('Route Type')}</TableHeader>
                                    <TableHeader>{t('Boat')}</TableHeader>
                                    <TableHeader>{t('Trip')}</TableHeader>
                                    <TableHeader>{t('Captain')}</TableHeader>
                                    <TableHeader>{t('Trip Time')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader className="flex justify-end">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody className="font-semibold">
                                {historyList.map((history, index) => {
                                    return (
                                        <TableRow
                                            className={`${
                                                history.status.code === 'SCH'
                                                    ? 'text-[#c78010]'
                                                    : history.status.code === 'ACT'
                                                    ? 'text-[#319d5d]'
                                                    : history.status.code === 'CMP'
                                                    ? 'text-[#423f3f]'
                                                    : 'text-[#a09b9b]'
                                            } cursor-pointer hover:bg-gray-50`}
                                            key={history.history_id}
                                            onClick={() =>
                                                navigate(`/passenger-record/${history.trip?.trip_id}/passengers`, {
                                                    state: {
                                                        trip_date: history.activity_date,
                                                        trip_name: history.trip?.trip_name?.en,
                                                    },
                                                })
                                            }
                                            title={`History #${history.history_id}`}
                                        >
                                            <TableCell>{customDateFormat(history.activity_date)}</TableCell>

                                            <TableCell>{history.route?.route_name?.en || '-'}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    color={
                                                        history.route?.master_route?.route_type?.code === 'INL'
                                                            ? 'amber'
                                                            : 'emerald'
                                                    }
                                                    className="capitalize"
                                                >
                                                    {history.route?.master_route?.route_type?.description?.en || '-'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{history.boat?.name?.en || '-'}</TableCell>
                                            <TableCell>
                                                {history.trip?.trip_name?.en || '-'}
                                                {history.is_trip_private && (
                                                    <Badge color="rose" className="ms-2">{t('Private')}</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {history.captain?.user_info?.given_name || '-'}{' '}
                                                {history.captain?.user_info?.family_name}
                                            </TableCell>
                                            <TableCell>
                                                {utcDateTimeToBahrainTime(history.start_time) || '-'} -{' '}
                                                {utcDateTimeToBahrainTime(history.end_time) || '-'}
                                            </TableCell>

                                            <TableCell>
                                                <Badge
                                                    color={
                                                        history.status.code === 'SCH'
                                                            ? 'orange'
                                                            : history.status.code === 'INA'
                                                            ? 'purple'
                                                            : history.status.code === 'ACT'
                                                            ? 'emerald'
                                                            : 'zinc'
                                                    }
                                                    className="capitalize"
                                                >
                                                    {history.status?.description?.en || '-'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Dropdown>
                                                    <DropdownButton
                                                        plain
                                                        aria-label="More options"
                                                        onClick={e => e.stopPropagation()}
                                                        className="cursor-pointer"
                                                    >
                                                        <EllipsisVerticalIcon />
                                                    </DropdownButton>
                                                    <DropdownMenu anchor="bottom">
                                                        <DropdownItem
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                navigate(
                                                                    `/passenger-record/${history.trip?.trip_id}/passengers`,
                                                                    {
                                                                        state: {
                                                                            trip_date: history.activity_date,
                                                                            trip_name: history.trip?.trip_name?.en,
                                                                        },
                                                                    }
                                                                )
                                                            }}
                                                        >
                                                            <EyeIcon />
                                                            <DropdownLabel>{t('View')}</DropdownLabel>
                                                        </DropdownItem>
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}
        </>
    )
}
